import assertNever from "assert-never";
import classNames from "classnames";
import * as immutable from "immutable";
import * as React from "react";
import { deepEqual } from "../deep-equal";
import * as google_maps_api from "../google_maps_api";
import { loading } from "../Loading";
import { BUS_ICON, PERSON_WALKING_ICON, SPORTS_CAR_ICON } from "../utils/svgs_new";
import { AppIcon } from "./Generic/AppIcon";
import MapWithLocationsCenter from "./MapWithLocationsCenter";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { AppButton } from "./Generic/AppButton";
import styled from "@emotion/styled";

export enum TravelMode {
  DRIVING,
  TRANSIT,
  WALKING,
}

interface Tabs {
  travelMode: TravelMode;
  icon: JSX.Element;
}

const tabs: Tabs[] = [
  {
    travelMode: TravelMode.DRIVING,
    icon: SPORTS_CAR_ICON,
  },
  {
    travelMode: TravelMode.WALKING,
    icon: PERSON_WALKING_ICON,
  },
  {
    travelMode: TravelMode.TRANSIT,
    icon: BUS_ICON,
  },
];

export interface MapWidgetProps {
  origin: google.maps.LatLngLiteral;
  destination: google.maps.LatLngLiteral;
  patientInitials: string;
  onOpenMapClick: () => void;
}

export interface MapWidgetState {
  googleMapsApiLoaded: boolean;
  directionsResults: immutable.Map<TravelMode, google.maps.DirectionsResult>;
  calcRoutes: (travelMode: number | null) => void;
  previousDestination: google.maps.LatLngLiteral | null;
  previousOrigin: google.maps.LatLngLiteral | string | null;
  previousTravelMode: TravelMode | null;
  travelMode: TravelMode | null;
}

/**
 * Displays a MapWithDirections, and below it buttons for switching between
 * transportation modes
 */
export class MapWidget extends React.Component<MapWidgetProps, MapWidgetState> {
  constructor(props: MapWidgetProps) {
    super(props);

    this.state = {
      googleMapsApiLoaded: false,
      directionsResults: immutable.Map<TravelMode, google.maps.DirectionsResult>(),
      calcRoutes: this.calcRoutes.bind(this),
      previousDestination: null,
      previousOrigin: null,
      previousTravelMode: null,
      travelMode: null,
    };
  }

  mounted: boolean = false;

  // TODO !!! This looks broken, directionsService may be used before it is initialized ("handleTabChange" flow)
  directionsService!: google.maps.DirectionsService;

  componentDidMount() {
    this.mounted = true;

    google_maps_api.onload(() => {
      if (!this.mounted) {
        return;
      }

      this.directionsService = new google.maps.DirectionsService();

      this.setState((_prevState: MapWidgetState) => {
        return {
          googleMapsApiLoaded: true,
        };
      });
    });
    this.noScrolling((window as any).mapWidget);
  }

  convertTravelMode = (travelMode: TravelMode): google.maps.TravelMode => {
    switch (travelMode) {
      case TravelMode.DRIVING:
        return google.maps.TravelMode.DRIVING;
      case TravelMode.TRANSIT:
        return google.maps.TravelMode.TRANSIT;
      case TravelMode.WALKING:
        return google.maps.TravelMode.WALKING;
      default:
        return assertNever(travelMode);
    }
  };

  async calcRoutes(travelMode: number | null) {
    if (travelMode !== null) {
      const t = tabs.filter((tab) => tab.travelMode === travelMode)[0];

      // To make sure set state had happened
      await delay(0);

      // TODO Use TransitOptions.arrivalTime (we will need a new field in MapWidgetProps)
      const result = await this.getRouteWithBackoff(this.convertTravelMode(t.travelMode));

      // We are in an async function, so it's possible that the component has
      // in the meanwhile been unmounted
      if (!this.mounted || result === null) {
        return;
      }

      console.log("Got directions for ", t.travelMode);
      console.log(result.routes[0].legs[0].duration.value);

      this.setState((prevState: MapWidgetState) => {
        return {
          directionsResults: prevState.directionsResults.set(t.travelMode, result),
          previousDestination: this.props.destination,
          previousOrigin: this.props.origin,
          previousTravelMode: this.state.travelMode,
        };
      });
    }
  }

  async getRouteWithBackoff(
    travelMode: google.maps.TravelMode,
    backoff: number = 100
  ): Promise<any> {
    try {
      const result = await route(this.directionsService, {
        origin: this.props.origin,
        destination: this.props.destination,
        travelMode: travelMode,
      });

      return result;
    } catch {
      // Stop if component is no longer mounted
      if (this.mounted) {
        await delay(backoff);
        return this.getRouteWithBackoff(travelMode, backoff + 100);
      } else {
        return null;
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if ((window as any).mapWidget !== null) {
      (window as any).mapWidget.removeEventListener("touchmove", (e: any) => e.preventDefault());
    }
  }

  handleTabChange = (activeTab: TravelMode) => {
    if (this.state.travelMode !== activeTab) {
      this.setState({ travelMode: activeTab });
      this.calcRoutes(activeTab);
    }
  };

  noScrolling = (comp: any) => {
    comp.addEventListener("touchmove", (e: any) => e.preventDefault(), false);
  };

  render() {
    return (
      <div className="MapWidget__root" ref={(comp) => ((window as any).mapWidget = comp)}>
        <div className="MapWidget__mapCont">
          {
            this.state.googleMapsApiLoaded ? (
              <MapWithLocationsCenter
                caregiverLocation={this.props.origin}
                visitLocation={this.props.destination}
                directionResult={(() => {
                  if (this.state.travelMode !== null) {
                    const directionsResult = this.state.directionsResults.get(
                      this.state.travelMode
                    );
                    if (directionsResult === undefined) {
                      return loading();
                    } else {
                      return directionsResult;
                    }
                  } else {
                    return loading();
                  }
                })()}
                patientInitials={this.props.patientInitials}
              />
            ) : (
              "Loading..."
            ) /* TODO */
          }
        </div>
        <div className="MapWidget__tabBarCont">
          <TabBar
            activeTab={this.state.travelMode}
            directionsResults={this.state.directionsResults}
            onChange={this.handleTabChange}
            destination={this.props.destination}
            onOpenMapClick={this.props.onOpenMapClick}
          />
        </div>
      </div>
    );
  }
}

/**
 * Promise API wrapper around google.maps.DirectionsService.route
 */
function route(
  directionsService: google.maps.DirectionsService,
  request: google.maps.DirectionsRequest
): Promise<google.maps.DirectionsResult> {
  return new Promise<google.maps.DirectionsResult>((resolve, reject) => {
    directionsService.route(request, (result, status) => {
      if (status !== google.maps.DirectionsStatus.OK) {
        reject(status);
        return;
      }

      resolve(result);
    });
  });
}

interface TabBarProps {
  activeTab: TravelMode | null;
  directionsResults: immutable.Map<TravelMode, google.maps.DirectionsResult>;
  destination: google.maps.LatLngLiteral;
  onChange: (activeTab: TravelMode) => void;
  onOpenMapClick: () => void;
}

class TabBar extends React.Component<TabBarProps, {}> {
  shouldComponentUpdate(props: TabBarProps) {
    return (
      props.activeTab !== this.props.activeTab ||
      !deepEqual(props.directionsResults, this.props.directionsResults)
    );
  }
  render() {
    return (
      <div className="MapWidget__TabBar__root">
        {tabs.map((tab, index) => (
          <div
            key={index}
            onClick={() => this.props.onChange(tab.travelMode)}
            className={classNames("MapWidget__TabBar__tab", {
              MapWidget__TabBar__tab__active: this.props.activeTab === tab.travelMode,
            })}
          >
            <AppIcon
              icon={tab.icon}
              fill={
                this.props.activeTab === tab.travelMode ? "var(--primary-color)" : "var(--gray-500)"
              }
            />

            {(() => {
              const directionResults = this.props.directionsResults.get(tab.travelMode);
              if (directionResults !== undefined && this.props.activeTab === tab.travelMode) {
                return <div>{directionResults.routes[0].legs[0].duration.text}</div>;
              } else {
                return;
              }
            })()}
          </div>
        ))}
        <MapWidgetMapButton onClick={this.props.onOpenMapClick} size="extra-small">
          {translate(STRINGS.OPEN_MAPS)}
        </MapWidgetMapButton>
      </div>
    );
  }
}

function delay(milliseconds: number): Promise<void> {
  return new Promise<void>((resolve, _reject) => {
    setTimeout(resolve, milliseconds);
  });
}

const MapWidgetMapButton = styled(AppButton)`
  margin-inline-end: 8px;
  align-self: center;
`;
